import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Kanban, ArrowClockwise, WarningCircle, CheckCircle, ClockCounterClockwise, Fire, Bug, ArrowRight, Target, Flag } from "@phosphor-icons/react";

const CATEGORY_COLOR = {
  new: "bg-slate-500",
  indeterminate: "bg-blue-500",
  done: "bg-emerald-500",
  undefined: "bg-slate-400",
};

const CATEGORY_LABEL = {
  new: "To Do",
  indeterminate: "In Progress",
  done: "Done",
  undefined: "Uncategorised",
};

const priorityTone = (name) => {
  if (name === "Highest") return "bg-red-100 text-red-700";
  if (name === "High") return "bg-orange-100 text-orange-700";
  if (name === "Medium") return "bg-amber-100 text-amber-700";
  if (name === "Low") return "bg-blue-100 text-blue-700";
  if (name === "Lowest") return "bg-slate-100 text-slate-600";
  return "bg-slate-100 text-slate-500";
};

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

export default function JiraSummaryPage() {
  const [status, setStatus] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectKey, setProjectKey] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("project"); // "project" or "portfolio"
  const [portfolio, setPortfolio] = useState(null);

  const loadPortfolio = async () => {
    setBusy(true); setError(null);
    try { const { data } = await api.get("/integrations/jira/portfolio"); setPortfolio(data); }
    catch (e) { const d = e?.response?.data?.detail; const m = typeof d === "string" ? d : (d?.message || "Failed to load portfolio"); setError(m); toast.error(m); }
    finally { setBusy(false); }
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/integrations/jira");
      setStatus(data);
      return data?.configured;
    } catch (e) { setStatus({ configured: false }); return false; }
  };

  const loadProjects = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/jira/projects");
      const list = data?.projects || [];
      setProjects(list);
      if (list.length && !projectKey) {
        setProjectKey(list[0].key);
        await loadSummary(list[0].key);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Failed to load projects"));
    } finally { setBusy(false); }
  };

  const loadSummary = async (key) => {
    if (!key) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.get(`/integrations/jira/project_summary?project_key=${encodeURIComponent(key)}`);
      setSummary(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Failed to load project summary");
      setError(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  useEffect(() => { (async () => { const ok = await loadStatus(); if (ok) await loadProjects(); })(); }, []);

  if (status && !status.configured) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl" data-testid="jira-summary-page">
        <div className="label-micro">Delivery · Live from Jira</div>
        <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Project summary</h1>
        <div className="mt-6 border-l-4 border-orange-500 bg-orange-50 p-4 rounded-sm">
          <div className="flex items-start gap-2">
            <WarningCircle size={20} className="text-orange-600 mt-0.5"/>
            <div>
              <div className="font-semibold text-orange-900">Jira is not connected</div>
              <p className="text-sm text-orange-800 mt-1">Ask an admin to connect Jira in <b>Integrations</b> (site URL, email, API token) to see a project-manager view of every Jira project.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="jira-summary-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">Delivery · Live from Jira</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Project summary</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">A project-manager view of every Jira project — health, priorities, workload by teammate, overdue work, and recent activity — pulled live from your Atlassian workspace.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-slate-100 rounded-sm p-0.5" data-testid="jira-mode-toggle">
            <button onClick={() => setMode("project")} data-testid="jira-mode-project"
                    className={`text-xs px-3 py-1.5 rounded-sm ${mode === "project" ? "bg-white shadow-sm font-bold" : "text-slate-600"}`}>Project</button>
            <button onClick={() => { setMode("portfolio"); if (!portfolio) loadPortfolio(); }} data-testid="jira-mode-portfolio"
                    className={`text-xs px-3 py-1.5 rounded-sm ${mode === "portfolio" ? "bg-white shadow-sm font-bold" : "text-slate-600"}`}>Portfolio</button>
          </div>
          {mode === "project" && (
            <select value={projectKey} onChange={(e) => { setProjectKey(e.target.value); loadSummary(e.target.value); }}
                    disabled={busy || projects.length === 0} data-testid="jira-project-selector"
                    className="border border-slate-300 rounded-sm px-3 py-2 text-sm min-w-[220px]">
              {projects.length === 0 && <option value="">No projects</option>}
              {projects.map(p => <option key={p.key} value={p.key}>{p.name} · {p.key}</option>)}
            </select>
          )}
          <button onClick={() => mode === "project" ? loadSummary(projectKey) : loadPortfolio()} disabled={busy} data-testid="jira-refresh"
                  className="text-sm inline-flex items-center gap-2 border border-slate-300 rounded-sm px-3 py-2 hover:bg-slate-100 disabled:opacity-50">
            <ArrowClockwise size={14} className={busy ? "animate-spin" : ""}/>{busy ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 border-l-4 border-red-500 bg-red-50 p-3 rounded-sm text-sm text-red-800" data-testid="jira-error">{error}</div>
      )}

      {mode === "portfolio" && (
        <>
          {busy && !portfolio && <div className="mt-8 text-sm text-slate-500">Rolling up every project…</div>}
          {portfolio && (
            <div data-testid="jira-portfolio">
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
                <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="label-micro">Projects scanned</div><div className="font-display text-2xl font-black mt-1">{portfolio.projects_scanned}</div></div>
                <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="label-micro">Total issues</div><div className="font-display text-2xl font-black mt-1">{portfolio.total_issues}</div></div>
                <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="label-micro">In progress</div><div className="font-display text-2xl font-black mt-1 text-blue-700">{portfolio.in_progress_count}</div></div>
                <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="label-micro">Done ({portfolio.completion_pct}%)</div><div className="font-display text-2xl font-black mt-1 text-emerald-700">{portfolio.done_count}</div><div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{width: `${portfolio.completion_pct}%`}}/></div></div>
                <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="label-micro">Overdue</div><div className="font-display text-2xl font-black mt-1 text-red-600">{portfolio.overdue_count}</div><div className="text-[11px] text-slate-500 mt-0.5">{portfolio.stale_count} stale</div></div>
              </div>
              <div className="mt-6 bg-white border border-slate-200 rounded-sm" data-testid="portfolio-projects-table">
                <div className="px-5 py-3 border-b border-slate-200"><h2 className="font-display font-bold">Projects by risk</h2><p className="text-xs text-slate-500 mt-0.5">Sorted by overdue count. Green fill is completion %.</p></div>
                <ul className="divide-y divide-slate-100">
                  {portfolio.projects.map(p => (
                    <li key={p.key} className="px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`portfolio-project-${p.key}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{p.name} <span className="text-xs text-slate-500 font-normal">· {p.key}</span></div>
                        <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-md"><div className="h-full bg-emerald-500" style={{width: `${p.completion_pct}%`}}/></div>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-slate-500">{p.total_issues} total</span>
                        <span className="text-slate-700 font-semibold">{p.open_count} open</span>
                        {p.overdue_count > 0 && <span className="text-red-700 font-bold">{p.overdue_count} overdue</span>}
                        <span className="font-display font-black">{p.completion_pct}%</span>
                        <button onClick={() => { setMode("project"); setProjectKey(p.key); loadSummary(p.key); }}
                                data-testid={`portfolio-drill-${p.key}`}
                                className="text-blue-700 hover:underline font-semibold">Drill in →</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              {portfolio.errors && portfolio.errors.length > 0 && (
                <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-3 rounded-sm text-xs text-amber-800">
                  <div className="font-semibold">Some projects couldn't be scanned:</div>
                  <ul className="mt-1 list-disc list-inside">{portfolio.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {mode === "project" && busy && !summary && <div className="mt-8 text-sm text-slate-500">Fetching project data from Jira…</div>}

      {mode === "project" && summary && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6" data-testid="jira-kpis">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="label-micro flex items-center gap-1"><Kanban size={12}/>Total issues</div>
              <div className="font-display text-2xl font-black mt-1 text-slate-900">{summary.total_issues}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="label-micro">Open</div>
              <div className="font-display text-2xl font-black mt-1 text-slate-700">{summary.open_count}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="label-micro">In progress</div>
              <div className="font-display text-2xl font-black mt-1 text-blue-700">{summary.in_progress_count}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="label-micro flex items-center gap-1"><CheckCircle size={12} className="text-emerald-600"/>Done ({summary.completion_pct}%)</div>
              <div className="font-display text-2xl font-black mt-1 text-emerald-700">{summary.done_count}</div>
              <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{width: `${summary.completion_pct}%`}}/></div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="label-micro flex items-center gap-1"><Fire size={12} className="text-red-500"/>Overdue</div>
              <div className="font-display text-2xl font-black mt-1 text-red-600">{summary.overdue_count}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{summary.stale_count} stale &gt; {summary.stale_threshold_days}d</div>
            </div>
          </div>

          {/* Active sprint snapshot */}
          {summary.active_sprints && summary.active_sprints.length > 0 && (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="jira-sprint-snapshot">
              {summary.active_sprints.map(s => (
                <div key={s.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`sprint-${s.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <h2 className="font-display font-bold flex items-center gap-2"><Target size={16} className="text-blue-600"/>{s.name}</h2>
                      {s.goal && <p className="text-xs text-slate-600 mt-1 italic">"{s.goal}"</p>}
                    </div>
                    <span className={`text-[11px] font-bold px-2 py-1 rounded-sm ${s.on_track ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {s.on_track ? "On track" : "Behind"}{s.days_left !== null && ` · ${s.days_left}d left`}
                    </span>
                  </div>

                  {/* Committed vs completed */}
                  <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                    <div><div className="label-micro">Committed</div><div className="font-display font-black text-xl mt-0.5">{s.committed_points} pts</div><div className="text-[11px] text-slate-500">{s.committed_issues} issues</div></div>
                    <div><div className="label-micro">Completed</div><div className="font-display font-black text-xl mt-0.5 text-emerald-700">{s.done_points} pts</div><div className="text-[11px] text-slate-500">{s.done_issues} done</div></div>
                    <div><div className="label-micro">Remaining</div><div className="font-display font-black text-xl mt-0.5 text-slate-700">{(s.committed_points - s.done_points).toFixed(1)} pts</div><div className="text-[11px] text-slate-500">{s.committed_issues - s.done_issues} left</div></div>
                  </div>

                  {/* Points burn (completed % vs time %) */}
                  <div className="mt-4">
                    <div className="flex justify-between text-[11px] text-slate-500 mb-1"><span>Points completed</span><b>{s.points_pct}%</b></div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all" style={{width: `${s.points_pct}%`}}/>
                    </div>
                    <div className="flex justify-between text-[11px] text-slate-500 mt-2 mb-1"><span>Time elapsed</span><b>{s.time_pct}%</b></div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${s.time_pct > s.points_pct + 10 ? "bg-red-400" : "bg-blue-500"}`} style={{width: `${s.time_pct}%`}}/>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">{s.on_track ? "Delivery pace matches the calendar." : "Delivery is trailing the calendar — reprioritise or descope."}</p>
                  </div>

                  {/* Mini status split */}
                  <div className="mt-4 flex h-2 rounded-full overflow-hidden bg-slate-100">
                    {s.done_issues > 0 && <div className="bg-emerald-500" style={{width: `${(s.done_issues/s.committed_issues)*100}%`}} title={`Done: ${s.done_issues}`}/>}
                    {s.in_progress_issues > 0 && <div className="bg-blue-500" style={{width: `${(s.in_progress_issues/s.committed_issues)*100}%`}} title={`In progress: ${s.in_progress_issues}`}/>}
                    {s.todo_issues > 0 && <div className="bg-slate-400" style={{width: `${(s.todo_issues/s.committed_issues)*100}%`}} title={`To do: ${s.todo_issues}`}/>}
                  </div>
                  <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                    <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1"></span>{s.done_issues} done</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1"></span>{s.in_progress_issues} in progress</span>
                    <span><span className="inline-block w-2 h-2 rounded-full bg-slate-400 mr-1"></span>{s.todo_issues} to do</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Epic rollup */}
          {summary.epics && summary.epics.length > 0 && (
            <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5" data-testid="jira-epic-rollup">
              <div className="flex items-center gap-2 mb-1">
                <Flag size={16} className="text-purple-600"/>
                <h2 className="font-display font-bold">Epic rollup</h2>
                <span className="ml-2 text-xs text-slate-500">{summary.epics.length} epic{summary.epics.length === 1 ? "" : "s"}</span>
              </div>
              <p className="text-xs text-slate-500 mb-4">Feature-level progress. Green fill is issues done; the number on the right is story points delivered.</p>
              <div className="space-y-3">
                {summary.epics.map(e => (
                  <div key={e.key} className="border border-slate-100 rounded-sm p-3" data-testid={`epic-${e.key}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <a href={status?.site_url ? `${status.site_url}/browse/${e.key}` : "#"} target="_blank" rel="noopener noreferrer"
                             className="text-xs font-bold text-blue-700 hover:underline">{e.key}</a>
                          {e.status && (
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${e.status_category === "done" ? "bg-emerald-100 text-emerald-700" : e.status_category === "indeterminate" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"}`}>{e.status}</span>
                          )}
                        </div>
                        <div className="text-sm font-semibold text-slate-900 mt-1 truncate">{e.name}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display font-black text-lg">{e.completion_pct}%</div>
                        <div className="text-[10px] text-slate-500">{e.done_issues} / {e.total_issues} issues</div>
                      </div>
                    </div>
                    <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all" style={{width: `${e.completion_pct}%`}}/>
                    </div>
                    {e.sp_total > 0 && (
                      <div className="mt-1 flex justify-between text-[11px] text-slate-500">
                        <span>{e.in_progress_issues} in progress · {e.todo_issues} to do</span>
                        <span><b className="text-slate-700">{e.sp_done}</b> / {e.sp_total} pts ({e.sp_pct}%)</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            {/* Status categories */}
            <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="jira-status-breakdown">
              <h2 className="font-display font-bold flex items-center gap-2"><Kanban size={16}/>Status breakdown</h2>
              <p className="text-xs text-slate-500 mt-0.5 mb-4">Grouped by Jira status category.</p>
              <div className="space-y-4">
                {summary.by_status_category.map(cat => (
                  <div key={cat.key} data-testid={`status-cat-${cat.key}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${CATEGORY_COLOR[cat.key] || "bg-slate-400"}`}></span>
                        <span className="font-semibold text-sm">{CATEGORY_LABEL[cat.key] || cat.label}</span>
                      </div>
                      <span className="text-sm font-black">{cat.count}</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${CATEGORY_COLOR[cat.key] || "bg-slate-400"}`} style={{width: `${(cat.count / Math.max(1, summary.total_issues)) * 100}%`}}/>
                    </div>
                    {cat.statuses && cat.statuses.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {cat.statuses.map(s => (
                          <span key={s.name} className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm">
                            {s.name} · {s.count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Priority breakdown */}
            <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="jira-priority-breakdown">
              <h2 className="font-display font-bold flex items-center gap-2"><Bug size={16}/>Priority mix</h2>
              <p className="text-xs text-slate-500 mt-0.5 mb-4">All issues in the project.</p>
              <div className="space-y-2">
                {summary.by_priority.map(p => {
                  const pct = Math.round((p.count / Math.max(1, summary.total_issues)) * 100);
                  return (
                    <div key={p.name} className="flex items-center gap-3" data-testid={`priority-${p.name}`}>
                      <span className={`text-xs font-bold px-2 py-1 rounded-sm min-w-[80px] text-center ${priorityTone(p.name)}`}>{p.name}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-slate-500" style={{width: `${pct}%`}}/>
                      </div>
                      <span className="text-sm font-black min-w-[50px] text-right">{p.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Assignee workload */}
          <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5" data-testid="jira-assignee-workload">
            <h2 className="font-display font-bold">Workload by teammate</h2>
            <p className="text-xs text-slate-500 mt-0.5 mb-4">Sorted by open issues. Overdue items flagged.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.by_assignee.map(a => (
                <div key={a.name} className="flex items-center gap-3 p-3 border border-slate-100 rounded-sm" data-testid={`assignee-${a.name}`}>
                  {a.avatar_url ? (
                    <img src={a.avatar_url} alt="" className="w-8 h-8 rounded-full"/>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-200 grid place-items-center text-[11px] font-bold text-slate-600">
                      {a.name.split(" ").map(x => x[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{a.name}</div>
                    <div className="text-[11px] text-slate-500">{a.open_count} open · {a.count} total</div>
                  </div>
                  {a.overdue_count > 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-100 text-red-700 rounded-sm shrink-0" title="Overdue">
                      🔥 {a.overdue_count}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            {/* Overdue */}
            {summary.overdue_issues && summary.overdue_issues.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-sm" data-testid="jira-overdue-panel">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                  <Fire size={16} className="text-red-500"/>
                  <h2 className="font-display font-bold">Overdue issues</h2>
                  <span className="ml-2 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-semibold">{summary.overdue_issues.length}</span>
                </div>
                <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {summary.overdue_issues.map(i => (
                    <div key={i.key} className="px-5 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <a href={status?.site_url ? `${status.site_url}/browse/${i.key}` : "#"} target="_blank" rel="noopener noreferrer"
                           className="text-xs font-bold text-blue-700 hover:underline">{i.key}</a>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-100 text-red-700 rounded-sm">{i.days_overdue}d late</span>
                      </div>
                      <div className="text-sm text-slate-800 mt-1 line-clamp-2">{i.summary}</div>
                      <div className="text-[11px] text-slate-500 mt-1">{i.assignee} · {i.status} · {i.priority}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent activity */}
            <div className="bg-white border border-slate-200 rounded-sm" data-testid="jira-recent-activity">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <ClockCounterClockwise size={16} className="text-slate-600"/>
                <h2 className="font-display font-bold">Recent activity</h2>
                <span className="ml-2 text-xs text-slate-500">Last 20 updates</span>
              </div>
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {summary.recent_activity.map(i => (
                  <div key={i.key + i.updated} className="px-5 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <a href={status?.site_url ? `${status.site_url}/browse/${i.key}` : "#"} target="_blank" rel="noopener noreferrer"
                         className="text-xs font-bold text-blue-700 hover:underline">{i.key}</a>
                      <span className="text-[11px] text-slate-500">{relTime(i.updated)}</span>
                    </div>
                    <div className="text-sm text-slate-800 mt-1 line-clamp-2">{i.summary}</div>
                    <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${CATEGORY_COLOR[i.status_category] || "bg-slate-400"}`}></span>
                      {i.status} <ArrowRight size={9}/> {i.assignee}
                      {i.is_overdue && <span className="text-[10px] font-bold px-1 bg-red-100 text-red-700 rounded-sm">overdue</span>}
                      {i.is_stale && !i.is_overdue && <span className="text-[10px] font-bold px-1 bg-amber-100 text-amber-700 rounded-sm">stale</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
